import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import useNotification from "../hooks/useNotification";
import UserService from "../services/userManagementService";

interface ResetPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  open,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const { showNotification } = useNotification();

  const handleResetPassword = async () => {
    if (!email) {
      setError("Please enter your email address.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await UserService.initiatePasswordReset(email);
      showNotification(
        "Password reset instructions have been sent to your email.",
        "success"
      );
      setResetSent(true);
      onClose();
    } catch (err) {
      setError("Failed to initiate password reset. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    setEmail("");
    setError(null);
    setResetSent(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        <FontAwesomeIcon icon={faLock} style={{ marginRight: "10px" }} />
        Reset Password
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {resetSent ? (
          <Typography variant="body1">
            If an account exists for the email address you entered, you will
            receive password reset instructions shortly.
          </Typography>
        ) : (
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleResetPassword} disabled={isLoading || !email}>
          {isLoading ? <CircularProgress size={24} /> : "Reset Password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResetPasswordDialog;
